/**
 * tapWake — wakes the Unity host on taps, and holds it awake through interaction settles.
 *
 * THE PROBLEM. Third face of the idle-governor family (dragWake.ts has the mechanics): NO tap
 * inside the embedded WebView ever wakes the host — pointer events are consumed by the WKWebView,
 * a tap-to-select is a pure store write with no bridge verb, and Unity's own input never sees any
 * of it. So a user who has been reading for >0.6s taps a shot into an already-throttled host
 * (20fps + 45ms/frame macOS main-thread sleep), and the settle that tap triggers — panel slide,
 * scene expand, 280ms tile FLIP, video mount — runs starved. Device-measured 20260824: seconds of
 * 44-67ms host frames bracketing selections, including a 3s 58-67ms stretch.
 *
 * TWO EXPORTS, one problem each:
 *  - installTapWakeKeepalive(): window capture+passive pointerdown listener, one ping per 300ms.
 *    Covers the whole class — every tap-triggered transition starts against an awake host.
 *  - wakeHostThroughSettle(): a fixed-count burst (now, +400ms, +800ms, +1200ms) for the
 *    interactions whose visual settle OUTLASTS the 0.6s window a single ping buys — shot
 *    select/deselect and scene disclosure. Pings through t=1.2s hold full rate to t=1.8s.
 *    Restartable: a new burst clears the previous schedule, so rapid taps never accumulate.
 *
 * Reuses the `scrollActivity` no-op verb — the two hosts that wake on receipt
 * (TimelineWebViewPanel, PurchasingWebViewPopupPanel) already recognise it, so this ships with
 * ZERO Unity-side changes. Same residual as its two siblings: the four OTHER embedded hosts
 * neither wake nor recognise the verb, so taps there log an unhandled-verb line and gain
 * nothing — one fix for the whole family when those hosts grow the receipt intercept.
 *
 * ZERO IDLE COST is the contract (the governor exists to prevent an idle 100%-CPU app, and this
 * module must never erode that): nothing polls, the burst is a fixed count of timeouts that
 * provably ends, and the listener runs only when the OS delivers a pointerdown. The burst
 * additionally no-ops unless the keepalive was installed (i.e. the app booted through main.tsx) —
 * component test suites call the burst sites freely without scheduling stray timers.
 */

import { capabilities } from '@/capabilities.ts';
import { enqueueSendToNative } from '@/bridge/transport';

/** Same figure and reasoning as dragWake/scrollWake: 2x headroom inside the 0.6s idle window. */
const WAKE_INTERVAL_MS = 300;
/** Burst schedule after the immediate ping. Last ping + the 0.6s window ≈ 1.8s of full rate —
 *  covering the settle's staged commits, the 280-300ms animations, and the video mount. */
const SETTLE_BURST_DELAYS_MS = [400, 800, 1200];

let installed = false;
let lastSentTs = 0;
let burstTimers: number[] = [];

const sendWake = (): void => {
  lastSentTs = Date.now();
  enqueueSendToNative('scrollActivity', {});
};

const wakeOnPointerDown = (): void => {
  if (Date.now() - lastSentTs < WAKE_INTERVAL_MS) return;
  sendWake();
};

/** Hold the host awake through an interaction settle (~1.8s). Immediate ping is un-throttled on
 *  purpose: it is the message that has the host awake for the commit the caller is about to make. */
export function wakeHostThroughSettle(): void {
  if (!installed || !capabilities.bridge) return;
  for (const t of burstTimers) window.clearTimeout(t);
  burstTimers = SETTLE_BURST_DELAYS_MS.map((delay) => window.setTimeout(sendWake, delay));
  sendWake();
}

/** Installs the tap keepalive on `window` (capture + passive: observation must never be able to
 *  delay the tap it reports). No-op without the bridge. Returns an uninstall — used by the tests;
 *  production never uninstalls. */
export function installTapWakeKeepalive(): () => void {
  if (!capabilities.bridge) return () => {};

  installed = true;
  lastSentTs = 0; // a fresh install always wakes on its first tap
  window.addEventListener('pointerdown', wakeOnPointerDown, { capture: true, passive: true });
  return () => {
    installed = false;
    window.removeEventListener('pointerdown', wakeOnPointerDown, true);
    for (const t of burstTimers) window.clearTimeout(t);
    burstTimers = [];
  };
}
